import { Fragment } from "react";

import RenderIf from "common/components/RenderIf";
import Checkbox from "common/components/ui/Checkbox";
import { typedObjectKeys } from "common/helpers/utils";

import {
  AUDIENCE_PROFILE_STATES,
  CANCELLED_TARGET_STATES,
} from "../../../../../constants";

import type { CancelledTargetState } from "../../../../../types";
import type { AudienceStateFilterMenuContentProps } from "./AudienceStateFilterMenu";

const filterOptionClassName =
  "flex w-full min-w-0 items-center gap-x-1.5 p-2 text-left text-button-sm";

export default function AudienceStateFilterOptions({
  selectedStates,
  setSelectedStates,
  audienceWithCancellation,
}: AudienceStateFilterMenuContentProps) {
  function addStateFilters(
    states: AudienceStateFilterMenuContentProps["selectedStates"],
  ) {
    setSelectedStates([...selectedStates, ...states]);
  }

  function removeStateFilters(
    states: AudienceStateFilterMenuContentProps["selectedStates"],
  ) {
    setSelectedStates(
      selectedStates.filter((state) => !states.includes(state)),
    );
  }

  return (
    <div className="flex flex-col">
      <button
        type="button"
        className={filterOptionClassName}
        onClick={() => setSelectedStates([])}
      >
        <Checkbox isStyleOnly checked={!selectedStates.length} />
        <span className="min-w-0 flex-1 truncate">All audience</span>
        {audienceWithCancellation && (
          <span className="shrink-0 text-xs text-black-500">
            {audienceWithCancellation.length}
          </span>
        )}
      </button>

      {AUDIENCE_PROFILE_STATES.map(({ label, states }) => {
        const isCancelledState = states.includes("cancelled");
        const allStates = isCancelledState
          ? [...states, ...typedObjectKeys(CANCELLED_TARGET_STATES)]
          : states;
        const isChecked = selectedStates.some((selectedState) =>
          allStates.some((state) => selectedState === state),
        );
        const isIndeterminate =
          isCancelledState &&
          isChecked &&
          !typedObjectKeys(CANCELLED_TARGET_STATES).every((state) =>
            selectedStates.includes(state),
          );

        return (
          <Fragment key={label}>
            <button
              type="button"
              className={filterOptionClassName}
              onClick={() => {
                if (isChecked) {
                  removeStateFilters(allStates);
                  return;
                }
                addStateFilters(allStates);
              }}
            >
              <Checkbox
                isStyleOnly
                checked={isIndeterminate ? "indeterminate" : isChecked}
              />
              <span className="min-w-0 flex-1 truncate">{label}</span>
              {audienceWithCancellation && (
                <span className="shrink-0 text-xs text-black-500">
                  {
                    audienceWithCancellation.filter(({ state }) =>
                      states.includes(state),
                    ).length
                  }
                </span>
              )}
            </button>

            <RenderIf condition={isCancelledState}>
              {(
                Object.entries(CANCELLED_TARGET_STATES) as [
                  CancelledTargetState,
                  string,
                ][]
              ).map(([subState, subLabel]) => {
                const isSubStateChecked = selectedStates.includes(subState);

                return (
                  <button
                    key={subState}
                    type="button"
                    className={`${filterOptionClassName} pl-6`}
                    onClick={() => {
                      if (isSubStateChecked) {
                        removeStateFilters([subState]);
                        return;
                      }
                      addStateFilters([subState]);
                    }}
                  >
                    <Checkbox isStyleOnly checked={isSubStateChecked} />
                    <span className="min-w-0 flex-1 truncate">{subLabel}</span>
                    {audienceWithCancellation && (
                      <span className="shrink-0 text-xs text-black-500">
                        {
                          audienceWithCancellation.filter(
                            ({ cancellationDetails }) =>
                              cancellationDetails?.transition === subState,
                          ).length
                        }
                      </span>
                    )}
                  </button>
                );
              })}
            </RenderIf>
          </Fragment>
        );
      })}
    </div>
  );
}
